/**
 * Add, correct or remove one negotiated rate.
 *
 * The write half of the drawer's Rates table, which has been read-only since
 * July because `agreement_rate` had no write path at all (feedback item 72).
 *
 * ── A MODAL, from the table's own header ────────────────────────────────────
 * Same division the contact and rate-audit flows settled on: "moving those
 * workflows to modals makes the drawer much more useful as reference info and
 * allows for us to have cleaner workspace… for when details need to be added"
 * (Jeff, 2026-09-09). The drawer keeps the rate TABLE; entering one gets room.
 *
 * ── The form mirrors the parser's two coupled rules, and says so on screen ──
 * `rate_model` decides which price field is even asked for: a fixed rate needs
 * an amount, a dynamic one needs a discount off the public rate. Showing both
 * at once invites filling in both and wondering which won. The API enforces
 * this too — the form is the explanation, not the guard.
 *
 * ── The term is the default window ──────────────────────────────────────────
 * A rate almost always runs for the agreement's term, and typing the same two
 * dates onto every seasonal row is the kind of friction that ends with them
 * left wrong. Pre-filled from the agreement and freely editable, because a
 * seasonal rate is exactly the case where they differ.
 */
import { useState } from 'react';
import { Modal } from '@/features/crm/components/Modal';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

/** Mirrors AVAILABILITY_TYPES in customer-management-api/src/lib/hsp/
 *  agreement-rate. Duplicated because the two apps share no package; the API
 *  rejects anything it does not know, so drift fails as a 400 naming the valid
 *  list rather than as a bad write. */
const AVAILABILITY = [
  { value: 'unknown', label: 'Not confirmed yet' },
  { value: 'lra', label: 'LRA — last room available' },
  { value: 'non_lra', label: 'NLRA — not last room available' },
  // Added 2026-09-14. The sheet spells it as both values in one cell; 14 rows
  // of the real export carry it.
  { value: 'dual_load', label: 'Dual load — both' },
] as const;

const RATE_TYPES = [
  { value: 'negotiated', label: 'Negotiated' },
  { value: 'public', label: 'Public' },
  { value: 'consortia', label: 'Consortia' },
] as const;

export interface EditableRate {
  id: string;
  rateType: string;
  roomType: string | null;
  rateCode: string | null;
  season: string | null;
  extendedStayTier: string | null;
  rateModel: string;
  dynamicDiscountPct: string | null;
  rateIncludesVat: boolean | null;
  amount: string;
  currency: string;
  commissionPct: string | null;
  availabilityType: string;
  effectiveFrom: string;
  effectiveTo: string;
}

/** Form values — all strings, because a form holds text. */
type Values = Record<string, string>;

function valuesFor(
  rate: EditableRate | null,
  fallback: {
    termStart: string | null;
    termEnd: string | null;
    currency: string | null;
  },
): Values {
  const day = (v: string | null) => (v ?? '').slice(0, 10);
  if (!rate) {
    return {
      season: '',
      room_type: '',
      extended_stay_tier: '',
      rate_type: 'negotiated',
      rate_model: 'fixed',
      amount: '',
      currency: fallback.currency || 'USD',
      dynamic_discount_pct: '',
      commission_pct: '',
      availability_type: 'unknown',
      rate_includes_vat: '',
      rate_code: '',
      effective_from: day(fallback.termStart),
      effective_to: day(fallback.termEnd),
    };
  }
  return {
    season: rate.season ?? '',
    room_type: rate.roomType ?? '',
    extended_stay_tier: rate.extendedStayTier ?? '',
    rate_type: rate.rateType,
    rate_model: rate.rateModel,
    // A dynamic row stores 0.00 so the column stays NOT NULL; showing that as
    // the amount would read as a negotiated zero.
    amount: rate.rateModel === 'dynamic' ? '' : rate.amount,
    currency: rate.currency,
    dynamic_discount_pct: rate.dynamicDiscountPct ?? '',
    commission_pct: rate.commissionPct ?? '',
    availability_type: rate.availabilityType,
    rate_includes_vat:
      rate.rateIncludesVat === null ? '' : rate.rateIncludesVat ? 'yes' : 'no',
    rate_code: rate.rateCode ?? '',
    effective_from: day(rate.effectiveFrom),
    effective_to: day(rate.effectiveTo),
  };
}

export function RateForm({
  agreementId,
  agreementCurrency,
  onClose,
  onSaved,
  rate,
  termEnd,
  termStart,
  testId = 'agreement-rate-form',
}: {
  agreementId: string;
  agreementCurrency: string | null;
  onClose: () => void;
  onSaved: () => void;
  /** null to add, a rate to correct. */
  rate: EditableRate | null;
  termEnd: string | null;
  termStart: string | null;
  testId?: string;
}) {
  const [v, setV] = useState<Values>(() =>
    valuesFor(rate, { termStart, termEnd, currency: agreementCurrency }),
  );
  const [saving, setSaving] = useState<'save' | 'delete' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const set = (k: string, value: string) =>
    setV((prev) => ({ ...prev, [k]: value }));

  const dynamic = v.rate_model === 'dynamic';

  async function save() {
    // Guard the handler, not just the button: this form submits on Enter from
    // any of a dozen fields, and two identical rates are indistinguishable.
    if (saving) return;
    setSaving('save');
    setError(null);
    const body: Record<string, unknown> = {
      agreement_id: agreementId,
      rate_type: v.rate_type,
      room_type: v.room_type,
      rate_code: v.rate_code,
      season: v.season,
      extended_stay_tier: v.extended_stay_tier,
      rate_model: v.rate_model,
      currency: v.currency,
      commission_pct: v.commission_pct,
      availability_type: v.availability_type,
      effective_from: v.effective_from,
      effective_to: v.effective_to,
      // Only the field the model actually uses. Sending both would store a
      // stale one the next reader cannot tell from a real value.
      ...(dynamic
        ? { dynamic_discount_pct: v.dynamic_discount_pct }
        : { amount: v.amount }),
      // Three-state: absent is "unknown", which is not "confirmed excludes".
      ...(v.rate_includes_vat === ''
        ? {}
        : { rate_includes_vat: v.rate_includes_vat === 'yes' }),
    };
    try {
      await apiFetch(
        rate
          ? `/api/btp/agreement-rates/${rate.id}`
          : '/api/btp/agreement-rates',
        {
          method: rate ? 'PATCH' : 'POST',
          body: JSON.stringify(body),
        },
      );
      onSaved();
    } catch (err) {
      // The API's own message. Its parser is the authority on a bad decimal or
      // an impossible date, and it says which field and why.
      setError(messageOf(err, 'The rate could not be saved.'));
      setSaving(null);
    }
  }

  async function remove() {
    if (saving || !rate) return;
    if (
      !window.confirm(
        'Remove this rate? The agreement keeps a record of what was removed, but the row itself is gone.',
      )
    ) {
      return;
    }
    setSaving('delete');
    setError(null);
    try {
      await apiFetch(`/api/btp/agreement-rates/${rate.id}`, {
        method: 'DELETE',
      });
      onSaved();
    } catch (err) {
      setError(messageOf(err, 'The rate could not be removed.'));
      setSaving(null);
    }
  }

  const field = (
    key: string,
    label: string,
    extra: React.InputHTMLAttributes<HTMLInputElement> = {},
  ) => (
    <div>
      <label className="field-label" htmlFor={`${testId}-${key}`}>
        {label}
      </label>
      <input
        className="input"
        data-testid={`${testId}-${key}`}
        id={`${testId}-${key}`}
        onChange={(e) => set(key, e.target.value)}
        value={v[key]}
        {...extra}
      />
    </div>
  );

  return (
    <Modal
      actions={
        <>
          {rate && (
            <button
              className="btn btn-outline"
              data-testid={`${testId}-delete`}
              disabled={saving !== null}
              onClick={() => void remove()}
              style={{ marginRight: 'auto' }}
              type="button"
            >
              {saving === 'delete' ? 'Removing…' : 'Remove rate'}
            </button>
          )}
          <button
            className="btn btn-outline"
            data-testid={`${testId}-cancel`}
            disabled={saving !== null}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            data-testid={`${testId}-save`}
            disabled={saving !== null}
            onClick={() => void save()}
            type="button"
          >
            {saving === 'save' ? 'Saving…' : rate ? 'Save rate' : 'Add rate'}
          </button>
        </>
      }
      onClose={onClose}
      size="wide"
      testId={testId}
      title={rate ? 'Edit rate' : 'Add a rate'}
    >
      <form
        data-testid={`${testId}-form`}
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <p className="page-intro" style={{ marginTop: 0 }}>
          One row per season, room type and length-of-stay tier — that is the
          shape the RFP asks for, so several rows on one agreement is normal.
        </p>

        {error && (
          <div
            className="banner banner-error"
            data-testid={`${testId}-error`}
            role="alert"
          >
            {error}
          </div>
        )}

        <div className="form-grid">
          {field('season', 'Season', { placeholder: 'Peak, Off-peak…' })}
          {field('room_type', 'Room type', { placeholder: 'Standard king' })}
          {field('extended_stay_tier', 'Length-of-stay tier', {
            placeholder: '7+ nights',
          })}

          <div>
            <label className="field-label" htmlFor={`${testId}-rate_model`}>
              Priced as
            </label>
            <select
              className="select"
              data-testid={`${testId}-rate_model`}
              id={`${testId}-rate_model`}
              onChange={(e) => set('rate_model', e.target.value)}
              value={v.rate_model}
            >
              <option value="fixed">Fixed nightly rate</option>
              <option value="dynamic">Dynamic — a discount off public</option>
            </select>
          </div>

          {/* ONE price field, chosen by the model above. Showing both invites
              filling in both, and the stored row can only honour one. */}
          {dynamic
            ? field('dynamic_discount_pct', 'Discount off public (%)', {
                inputMode: 'decimal',
                placeholder: '12.50',
              })
            : field('amount', 'Nightly rate', {
                inputMode: 'decimal',
                placeholder: '189.00',
              })}
          {field('currency', 'Currency', { maxLength: 3 })}
          {field('commission_pct', 'Commission (%)', {
            inputMode: 'decimal',
            placeholder: '10',
          })}

          <div>
            <label
              className="field-label"
              htmlFor={`${testId}-availability_type`}
            >
              Availability
            </label>
            <select
              className="select"
              data-testid={`${testId}-availability_type`}
              id={`${testId}-availability_type`}
              onChange={(e) => set('availability_type', e.target.value)}
              value={v.availability_type}
            >
              {AVAILABILITY.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              className="field-label"
              htmlFor={`${testId}-rate_includes_vat`}
            >
              Rate includes VAT
            </label>
            <select
              className="select"
              data-testid={`${testId}-rate_includes_vat`}
              id={`${testId}-rate_includes_vat`}
              onChange={(e) => set('rate_includes_vat', e.target.value)}
              value={v.rate_includes_vat}
            >
              {/* Three-state. "Not asked" and "confirmed excludes VAT" are
                  opposite findings on an international rate. */}
              <option value="">— not confirmed —</option>
              <option value="yes">Includes VAT</option>
              <option value="no">Excludes VAT</option>
            </select>
          </div>

          <div>
            <label className="field-label" htmlFor={`${testId}-rate_type`}>
              Whose rate
            </label>
            <select
              className="select"
              data-testid={`${testId}-rate_type`}
              id={`${testId}-rate_type`}
              onChange={(e) => set('rate_type', e.target.value)}
              value={v.rate_type}
            >
              {RATE_TYPES.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {field('rate_code', 'Rate code', { placeholder: 'optional' })}
          {field('effective_from', 'Effective from', { type: 'date' })}
          {field('effective_to', 'Effective to', { type: 'date' })}
        </div>
      </form>
    </Modal>
  );
}

function messageOf(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? err.message;
  }
  return err instanceof Error ? err.message : fallback;
}
